import clsx from 'clsx';
import { useCallback } from 'react';

import { FormElementText } from '~components/generic/editor/FormElementText';
import IconLibrary from '~extension/icon-library';
import useLabels from '~hooks/useLabels';
import { IEntityComponentProps } from '~interfaces/props';
import { getModuleUrlIdFromData } from '~services/location-helpers';
import IconLibraryConfigFactory from './IconLibraryConfigFactory';
import styles from './IconLibrary.module.css';

export const ModuleIconLibrary = ({ data, editing, className, onChange }: IEntityComponentProps) => {
  const {
    hlIconLibrary,
    msgModuleAttributeIconLibraryRealmHelp,
    placeholderModuleAttributeIconLibraryRealm,
    labelModuleAttributeIconLibraryRealm,
    msgModuleAttributeIconLibrarySearchCategoriesHelp,
    placeholderModuleAttributeIconLibrarySearchCategories,
    labelModuleAttributeIconLibrarySearchCategories,
    msgModuleAttributeIconLibrarySvgFillColorsHelp,
    placeholderModuleAttributeIconLibrarySvgFillColors,
    labelModuleAttributeIconLibrarySvgFillColors,
    msgModuleAttributeIconLibraryDefaultSvgFillColorHelp,
    placeholderModuleAttributeIconLibraryDefaultSvgFillColor,
    labelModuleAttributeIconLibraryDefaultSvgFillColor,
    msgModuleAttributeIconLibrarySvgBackgroundColorsHelp,
    placeholderModuleAttributeIconLibrarySvgBackgroundColors,
    labelModuleAttributeIconLibrarySvgBackgroundColors,
    msgModuleAttributeIconLibraryDefaultSvgBackgroundColorHelp,
    placeholderModuleAttributeIconLibraryDefaultSvgBackgroundColor,
    labelModuleAttributeIconLibraryDefaultSvgBackgroundColor,
    msgModuleAttributeIconLibraryPngOutputSizesHelp,
    placeholderModuleAttributeIconLibraryPngOutputSizes,
    labelModuleAttributeIconLibraryPngOutputSizes,
    msgModuleAttributeIconLibraryListItemStylesHelp,
    placeholderModuleAttributeIconLibraryListItemStyles,
    labelModuleAttributeIconLibraryListItemStyles,
  } = useLabels([
    'hlIconLibrary',
    'msgModuleAttributeIconLibraryRealmHelp',
    'placeholderModuleAttributeIconLibraryRealm',
    'labelModuleAttributeIconLibraryRealm',
    'msgModuleAttributeIconLibrarySearchCategoriesHelp',
    'placeholderModuleAttributeIconLibrarySearchCategories',
    'labelModuleAttributeIconLibrarySearchCategories',
    'msgModuleAttributeIconLibrarySvgFillColorsHelp',
    'placeholderModuleAttributeIconLibrarySvgFillColors',
    'labelModuleAttributeIconLibrarySvgFillColors',
    'msgModuleAttributeIconLibraryDefaultSvgFillColorHelp',
    'placeholderModuleAttributeIconLibraryDefaultSvgFillColor',
    'labelModuleAttributeIconLibraryDefaultSvgFillColor',
    'msgModuleAttributeIconLibrarySvgBackgroundColorsHelp',
    'placeholderModuleAttributeIconLibrarySvgBackgroundColors',
    'labelModuleAttributeIconLibrarySvgBackgroundColors',
    'msgModuleAttributeIconLibraryDefaultSvgBackgroundColorHelp',
    'placeholderModuleAttributeIconLibraryDefaultSvgBackgroundColor',
    'labelModuleAttributeIconLibraryDefaultSvgBackgroundColor',
    'msgModuleAttributeIconLibraryPngOutputSizesHelp',
    'placeholderModuleAttributeIconLibraryPngOutputSizes',
    'labelModuleAttributeIconLibraryPngOutputSizes',
    'msgModuleAttributeIconLibraryListItemStylesHelp',
    'placeholderModuleAttributeIconLibraryListItemStyles',
    'labelModuleAttributeIconLibraryListItemStyles',
  ]);

  const handleOnChange = useCallback(
    (config: IconLibraryConfig) => (attr: string) => (value: string) => {
      const changedConfig = { ...config, [attr]: value };
      onChange('attributes.field_configuration', JSON.stringify(changedConfig));
    },
    [onChange],
  );

  // Do not render if there is no data!
  if (!data) {
    return null;
  }

  // Get field values
  const fieldConfigValue: string = data.attributes.field_configuration || '';
  const iconLibraryConfig = IconLibraryConfigFactory(fieldConfigValue);

  const handleConfigFieldChange = handleOnChange(iconLibraryConfig);

  // derived values
  const {
    defaultSvgBackgroundColor,
    defaultSvgFillColor,
    listItemStyles,
    pngOutputSizes,
    realm,
    searchCategories,
    svgBackgroundColors,
    svgFillColors,
  } = iconLibraryConfig;

  // ClassName generation
  const clsModule = clsx({ ...className.module });
  const clsContainer = clsx('relative px-0', { ...className.container });

  return (
    <div className={clsModule} id={getModuleUrlIdFromData(data.id, data.type)}>
      <div className={clsContainer}>
        {editing ? (
          <div className="row c-module__header">
            <div className="col u-margin-bottom-md container px-0">
              <div className="row">
                <h2>{hlIconLibrary}</h2>
              </div>
              <div className="row u-margin-vertical-sm">
                <div className="col col-6">
                  <FormElementText
                    value={realm}
                    onChange={handleConfigFieldChange('realm')}
                    label={labelModuleAttributeIconLibraryRealm}
                    placeholder={placeholderModuleAttributeIconLibraryRealm}
                    helpText={msgModuleAttributeIconLibraryRealmHelp}
                  />
                </div>
              </div>
              <div className="row u-margin-vertical-sm">
                <div className="col col-12">
                  <FormElementText
                    value={searchCategories}
                    onChange={handleConfigFieldChange('searchCategories')}
                    label={labelModuleAttributeIconLibrarySearchCategories}
                    placeholder={placeholderModuleAttributeIconLibrarySearchCategories}
                    helpText={msgModuleAttributeIconLibrarySearchCategoriesHelp}
                  />
                </div>
              </div>
              <div className="row u-margin-vertical-sm">
                <div className="col col-6">
                  <FormElementText
                    value={svgFillColors}
                    onChange={handleConfigFieldChange('svgFillColors')}
                    label={labelModuleAttributeIconLibrarySvgFillColors}
                    placeholder={placeholderModuleAttributeIconLibrarySvgFillColors}
                    helpText={msgModuleAttributeIconLibrarySvgFillColorsHelp}
                  />
                </div>
                <div className="col col-6">
                  <FormElementText
                    value={defaultSvgFillColor}
                    onChange={handleConfigFieldChange('defaultSvgFillColor')}
                    label={labelModuleAttributeIconLibraryDefaultSvgFillColor}
                    placeholder={placeholderModuleAttributeIconLibraryDefaultSvgFillColor}
                    helpText={msgModuleAttributeIconLibraryDefaultSvgFillColorHelp}
                  />
                </div>
              </div>
              <div className="row u-margin-vertical-sm">
                <div className="col col-6">
                  <FormElementText
                    value={svgBackgroundColors}
                    onChange={handleConfigFieldChange('svgBackgroundColors')}
                    label={labelModuleAttributeIconLibrarySvgBackgroundColors}
                    placeholder={placeholderModuleAttributeIconLibrarySvgBackgroundColors}
                    helpText={msgModuleAttributeIconLibrarySvgBackgroundColorsHelp}
                  />
                </div>
                <div className="col col-6">
                  <FormElementText
                    value={defaultSvgBackgroundColor}
                    onChange={handleConfigFieldChange('defaultSvgBackgroundColor')}
                    label={labelModuleAttributeIconLibraryDefaultSvgBackgroundColor}
                    placeholder={placeholderModuleAttributeIconLibraryDefaultSvgBackgroundColor}
                    helpText={msgModuleAttributeIconLibraryDefaultSvgBackgroundColorHelp}
                  />
                </div>
              </div>
              <div className="row u-margin-vertical-sm">
                <div className="col col-6">
                  <FormElementText
                    value={pngOutputSizes}
                    onChange={handleConfigFieldChange('pngOutputSizes')}
                    label={labelModuleAttributeIconLibraryPngOutputSizes}
                    placeholder={placeholderModuleAttributeIconLibraryPngOutputSizes}
                    helpText={msgModuleAttributeIconLibraryPngOutputSizesHelp}
                  />
                </div>
                <div className="col col-6">
                  <FormElementText
                    value={listItemStyles}
                    onChange={handleConfigFieldChange('listItemStyles')}
                    label={labelModuleAttributeIconLibraryListItemStyles}
                    placeholder={placeholderModuleAttributeIconLibraryListItemStyles}
                    helpText={msgModuleAttributeIconLibraryListItemStylesHelp}
                  />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <article className="row no-gutters">
            <section className={`block relative ${styles.section}`}>
              <div className="relative w-full h-full">
                <IconLibrary {...iconLibraryConfig} />
              </div>
            </section>
          </article>
        )}
      </div>
    </div>
  );
};

export default ModuleIconLibrary;
